import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { StatusBar } from '../components/load-workspace/StatusBar';
import '../components/load-workspace/workspace.css';
import { carrierAPI, loadAPI, documentAPI, interactionAPI } from '../api';
import { toast } from 'sonner';
import { toastApiError, highlightErrorFields } from '../lib/utils';
import { StatusBadge } from '../components/StatusBadge';
import { UpdatesDialog } from '../components/load-workspace/UpdatesDialog';
import { Phone, Mail, Upload, FileText, Loader2 } from 'lucide-react';

const CarrierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [carrier, setCarrier] = useState({
    name: '', mc_number: '', dot_number: '',
    address: '', city: '', state: '', zip_code: '',
    contact_name: '', contact_phone: '', contact_email: '',
    transfer_name: '', scac: '', caat: '',
    equipment_types: [], regions_served: [], classification: 'Regular'
  });
  const [drivers, setDrivers] = useState([]);
  const [loads, setLoads] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [updatesOpen, setUpdatesOpen] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const fileInputRef = useRef(null);


  const fetchCarrierData = useCallback(async () => {
    try {
      const [carrierRes, allLoads, docs, inters] = await Promise.all([
        carrierAPI.getById(id),
        loadAPI.getAll(),
        documentAPI.getByEntity('carrier', id),
        interactionAPI.getByEntity('carrier', id)
      ]);
      setCarrier(carrierRes.data);
      setDrivers(carrierRes.data.drivers || []);
      setLoads(allLoads.data.filter(l => l.carrier_id === id));
      setDocuments(docs.data);
      setInteractions(inters.data);
    } catch (error) {
      toast.error('Failed to load carrier data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) fetchCarrierData();
  }, [id, isNew, fetchCarrierData]);

  const handleSave = async () => {
    const missing = ['name', 'mc_number', 'contact_phone', 'contact_email'].filter(f => !(carrier[f] || '').trim());
    if (missing.length) {
      const labels = { name: 'Name', mc_number: 'MC #', contact_phone: 'Phone', contact_email: 'Email' };
      toast.error('Carrier could not be saved', { description: missing.map(f => `${labels[f]} is required`).join('\n'), duration: 9000, style: { whiteSpace: 'pre-line' } });
      highlightErrorFields(missing);
      return;
    }
    try {
      if (isNew) {
        await carrierAPI.create(carrier);
        toast.success('Carrier created successfully');
      } else {
        await carrierAPI.update(id, carrier);
        toast.success('Carrier updated successfully');
      }
      navigate('/carriers');
    } catch (error) {
      toastApiError(toast, error, 'Carrier could not be saved');
    }
  };

  const handleAddDriver = async () => {
    const name = prompt('Driver Name:');
    if (!name) return;
    const phone = prompt('Driver Phone:');
    if (!phone) return;
    const license = prompt('License Number (optional):');

    try {
      await carrierAPI.addDriver(id, { name, phone, license_number: license });
      toast.success('Driver added');
      fetchCarrierData();
    } catch (error) {
      toast.error('Failed to add driver');
    }
  };

  const handleAddNote = async () => {
    const content = prompt('Enter note:');
    if (content) {
      try {
        await interactionAPI.create({
          entity_type: 'carrier',
          entity_id: id,
          interaction_type: 'note',
          subject: 'Carrier Note',
          content,
          attachments: []
        });
        toast.success('Note added');
        fetchCarrierData();
      } catch (error) {
        toast.error('Failed to add note');
      }
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleDocumentUpload(file);
  };
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) handleDocumentUpload(file);
  };

  const handleDocumentUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    setUploadingDoc(true);
    try {
      await documentAPI.upload(file, 'carrier', id, 'compliance', `Carrier compliance document: ${file.name}`);
      toast.success(`"${file.name}" uploaded successfully`);
      await fetchCarrierData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center h-full"><div>Loading...</div></div></MainLayout>;
  }

  const set = (field) => (e) => setCarrier({ ...carrier, [field]: e.target.value });

  const classColor = { 'Partner Carrier': 'lightgreen', Regular: 'neutral', Loadboard: 'yellow' }[carrier.classification || 'Regular'] || 'neutral';

  return (
    <MainLayout>
      <div className="lw-root" style={{ minHeight: '100%' }} data-testid="carrier-detail-page">
        {/* Header */}
        <div className="lw-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span className="lw-link" onClick={() => navigate('/carriers')} data-testid="back-button">← Carriers</span>
            <span style={{ fontSize: 18, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isNew ? 'New Carrier' : carrier.name}
            </span>
            {!isNew && (
              <span style={{ color: 'var(--lw-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                MC: {carrier.mc_number} {carrier.dot_number && `| DOT: ${carrier.dot_number}`}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isNew && (
              <button className="lw-btn" onClick={() => setUpdatesOpen(true)} data-testid="updates-carrier-button">Updates</button>
            )}
            {!isNew && (
              <button
                className="lw-btn"
                style={{ color: 'var(--lw-red)', borderColor: 'var(--lw-red)' }}
                onClick={async () => {
                  const reason = window.prompt('Why are you deleting this carrier? (required)');
                  if (reason === null) return;
                  if (!reason.trim()) { toast.error('A reason is required to delete'); return; }
                  try {
                    await carrierAPI.delete(id, reason.trim());
                    toast.success('Carrier deleted');
                    navigate('/carriers');
                  } catch (e) { toast.error('Failed to delete carrier'); }
                }}
                data-testid="delete-carrier-button"
              >Delete</button>
            )}
            <button className="lw-btn" onClick={() => navigate('/carriers')} data-testid="cancel-button">Cancel</button>
            <button className="lw-btn primary" style={{ padding: '0 22px' }} onClick={handleSave} data-testid="save-carrier-button">
              Save Carrier
            </button>
          </div>
        </div>

        {!isNew && <UpdatesDialog open={updatesOpen} onOpenChange={setUpdatesOpen} entityType="carrier" entityId={id} />}

        <div className="lw-grid" data-testid="carrier-sections">
          {/* Company Information */}
          <div className="lw-panel" data-testid="section-company">
            <StatusBar left="Company Information" right={isNew ? 'New' : 'Profile'} color="blue" testId="car-statusbar-company" />
            <div className="lw-row">
              <span className="lw-label w95">Name *</span>
              <input className="lw-input" value={carrier.name} onChange={set('name')} data-testid="input-name" data-field="name" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">MC # *</span>
              <input className="lw-input" value={carrier.mc_number} onChange={set('mc_number')} data-testid="input-mc" data-field="mc_number" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">DOT #</span>
              <input className="lw-input" value={carrier.dot_number || ''} onChange={set('dot_number')} data-testid="input-dot" />
            </div>
            <div className="lw-sep" />
            <div className="lw-row">
              <span className="lw-label w95">Address</span>
              <input className="lw-input" value={carrier.address || ''} onChange={set('address')} placeholder="Street address" data-testid="input-address" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">City</span>
              <input className="lw-input" value={carrier.city || ''} onChange={set('city')} data-testid="input-city" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">State / ZIP</span>
              <input className="lw-input tiny" style={{ maxWidth: 60 }} placeholder="TX" maxLength={2} value={carrier.state || ''} onChange={set('state')} data-testid="input-state" />
              <input className="lw-input narrow" value={carrier.zip_code || ''} onChange={set('zip_code')} data-testid="input-zip" />
            </div>
          </div>

          {/* Contact & Compliance */}
          <div className="lw-panel" data-testid="section-contact">
            <StatusBar left="Contact & Compliance" right="Primary" color="lightblue" testId="car-statusbar-contact" />
            <div className="lw-row">
              <span className="lw-label w95">Contact</span>
              <input className="lw-input" value={carrier.contact_name || ''} onChange={set('contact_name')} placeholder="Primary contact person" data-testid="input-contact-name" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">Phone *</span>
              <input className="lw-input" value={carrier.contact_phone} onChange={set('contact_phone')} data-testid="input-phone" data-field="contact_phone" />
              {carrier.contact_phone && (
                <a href={`tel:${carrier.contact_phone.replace(/[^0-9+]/g, '')}`} className="lw-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }} data-testid="call-phone-button">
                  <Phone style={{ width: 13, height: 13 }} /> Call
                </a>
              )}
            </div>
            <div className="lw-row">
              <span className="lw-label w95">Email *</span>
              <input className="lw-input" type="email" value={carrier.contact_email} onChange={set('contact_email')} data-testid="input-email" data-field="contact_email" />
              {carrier.contact_email && (
                <a href={`mailto:${carrier.contact_email}`} className="lw-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }} data-testid="email-button">
                  <Mail style={{ width: 13, height: 13 }} /> Email
                </a>
              )}
            </div>
            <div className="lw-sep" />
            <div className="lw-row">
              <span className="lw-label w95">Transfer</span>
              <input className="lw-input" value={carrier.transfer_name || ''} onChange={set('transfer_name')} placeholder="Transfer agent name" data-testid="input-transfer-name" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">SCAC</span>
              <input className="lw-input" value={carrier.scac || ''} onChange={set('scac')} data-testid="input-scac" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">CAAT (XB)</span>
              <input className="lw-input" value={carrier.caat || ''} onChange={set('caat')} data-testid="input-caat" />
            </div>
          </div>

          {/* Classification & Performance */}
          <div className="lw-panel" data-testid="section-performance">
            <StatusBar
              left="Classification & KPIs"
              right={
                <select
                  className="lw-input"
                  style={{ height: 24, maxWidth: 150, fontSize: 12, fontWeight: 700 }}
                  value={carrier.classification || 'Regular'}
                  onChange={set('classification')}
                  data-testid="select-classification"
                >
                  <option value="Partner Carrier">Partner Carrier</option>
                  <option value="Regular">Regular</option>
                  <option value="Loadboard">Loadboard</option>
                </select>
              }
              color={classColor}
              testId="car-statusbar-performance"
            />
            <div className="lw-profitbox" style={{ marginBottom: 6 }}>
              <span>On-Time Pickup</span>
              <span style={{ color: 'var(--lw-green)', fontSize: 16 }} data-testid="kpi-otp">{carrier.on_time_pickup_pct?.toFixed(1) || 0}%</span>
            </div>
            <div className="lw-profitbox" style={{ marginBottom: 6 }}>
              <span>On-Time Delivery</span>
              <span style={{ color: '#0a3a75', fontSize: 16 }} data-testid="kpi-otd">{carrier.on_time_delivery_pct?.toFixed(1) || 0}%</span>
            </div>
            <div className="lw-profitbox">
              <span>Tracking Compliance</span>
              <span style={{ color: 'var(--tms-purple)', fontSize: 16 }} data-testid="kpi-tracking">{carrier.tracking_compliance_pct?.toFixed(1) || 0}%</span>
            </div>
            <p style={{ fontSize: 12, color: 'var(--lw-muted)', marginTop: 8 }}>
              KPIs are computed from completed load history.
            </p>
          </div>

          {/* Drivers */}
          {!isNew && (
            <div className="lw-panel" data-testid="section-drivers">
              <StatusBar left="Drivers" right={`${drivers.length}`} color="neutral" testId="car-statusbar-drivers" />
              <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                {drivers.length === 0 ? (
                  <p style={{ fontSize: 12.5, color: 'var(--lw-muted)', textAlign: 'center', padding: '14px 0' }}>No drivers assigned yet</p>
                ) : (
                  <table className="lw-fin-table" style={{ fontSize: 12.5 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Driver</th>
                        <th style={{ textAlign: 'left' }}>Phone</th>
                        <th style={{ textAlign: 'left' }}>License #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drivers.map((driver, idx) => (
                        <tr key={`${driver.name}-${driver.phone}`} data-testid={`driver-${idx}`}>
                          <td style={{ fontWeight: 700 }}>{driver.name}</td>
                          <td style={{ fontWeight: 400 }}>{driver.phone}</td>
                          <td style={{ fontWeight: 400 }}>{driver.license_number || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="lw-btnrow">
                <button className="lw-btn primary" onClick={handleAddDriver} data-testid="add-driver-button">+ Add Driver</button>
              </div>
            </div>
          )}

          {/* Documents & Compliance */}
          {!isNew && (
            <div className="lw-panel" data-testid="section-documents">
              <StatusBar left="Documents & Compliance" right={`${documents.length} file${documents.length === 1 ? '' : 's'}`} color="neutral" testId="car-statusbar-docs" />
              <div
                style={{ border: `2px dashed ${isDragging ? 'var(--lw-primary)' : 'var(--lw-border-strong)'}`, borderRadius: 4, padding: 14, textAlign: 'center', marginBottom: 8, background: isDragging ? '#fff0f4' : '#f9fafb', cursor: 'pointer', opacity: uploadingDoc ? 0.5 : 1 }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploadingDoc && fileInputRef.current?.click()}
                data-testid="doc-drop-zone"
              >
                <Upload style={{ width: 20, height: 20, margin: '0 auto 6px', color: 'var(--lw-muted)' }} />
                <p style={{ fontSize: 12.5, color: 'var(--lw-muted)' }}>{isDragging ? 'Drop file here...' : 'Drag & drop, or click to browse'}</p>
                <p style={{ fontSize: 11, color: 'var(--lw-muted)' }}>W9, Insurance, Authority — PDF, Images, Word, Excel (max 10MB)</p>
              </div>
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" />
              <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                {documents.length === 0 ? (
                  <p style={{ fontSize: 12.5, color: 'var(--lw-muted)', textAlign: 'center', padding: '10px 0' }}>No documents uploaded yet</p>
                ) : (
                  documents.map((doc) => (
                    <div key={doc.id} className="lw-row" style={{ border: '1px solid var(--lw-border)', borderRadius: 4, padding: 6 }} data-testid={`doc-${doc.id}`}>
                      <FileText style={{ width: 15, height: 15, color: 'var(--lw-primary)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</p>
                        <p style={{ fontSize: 11, color: 'var(--lw-muted)' }}>{doc.document_type} • Added {new Date(doc.created_at).toLocaleDateString()}</p>
                      </div>
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="lw-link" data-testid={`view-doc-${doc.id}`}>View</a>
                      <button
                        className="lw-btn"
                        style={{ height: 24, padding: '0 8px', color: 'var(--lw-red)' }}
                        onClick={() => {
                          if (window.confirm('Delete this document?')) {
                            documentAPI.delete(doc.id).then(() => { toast.success('Document deleted'); fetchCarrierData(); }).catch(() => toast.error('Failed to delete'));
                          }
                        }}
                        data-testid={`delete-doc-${doc.id}`}
                      >✕</button>
                    </div>
                  ))
                )}
              </div>
              <div className="lw-btnrow">
                <button className="lw-btn primary" onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} data-testid="add-document-button">
                  {uploadingDoc ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> Uploading...</span> : '+ Add Document'}
                </button>
              </div>
            </div>
          )}

          {/* Load Assignments */}
          {!isNew && (
            <div className="lw-panel" data-testid="section-assignments">
              <StatusBar left="Load Assignments" right={`${loads.length} load${loads.length === 1 ? '' : 's'}`} color="lightgreen" testId="car-statusbar-loads" />
              <div style={{ overflowY: 'auto', overflowX: 'auto', flexGrow: 1 }}>
                <table className="lw-fin-table" style={{ fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Load</th>
                      <th style={{ textAlign: 'left' }}>Customer</th>
                      <th style={{ textAlign: 'left' }}>Status</th>
                      <th style={{ textAlign: 'right' }}>Carrier Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loads.length === 0 ? (
                      <tr><td colSpan="4" style={{ textAlign: 'center', padding: '14px 0', color: 'var(--lw-muted)' }}>No loads assigned</td></tr>
                    ) : (
                      loads.map((load) => (
                        <tr key={load.id} data-testid={`load-${load.id}`}>
                          <td>
                            <span className="lw-link" onClick={() => navigate(`/loads/${load.id}`)}>
                              {load.load_number || load.id.substring(0, 8)}
                            </span>
                          </td>
                          <td style={{ fontWeight: 400, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{load.customer_name}</td>
                          <td><StatusBadge status={load.status} /></td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>${load.carrier_pay?.toLocaleString() || 0}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes & Interactions */}
          {!isNew && (
            <div className="lw-panel" data-testid="section-notes">
              <StatusBar left="Notes & Interactions" right={`${interactions.length}`} color="neutral" testId="car-statusbar-notes" />
              <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                {interactions.length === 0 ? (
                  <p style={{ fontSize: 12.5, color: 'var(--lw-muted)', textAlign: 'center', padding: '14px 0' }}>No interactions yet</p>
                ) : (
                  interactions.map((inter) => (
                    <div key={inter.id} style={{ border: '1px solid var(--lw-border)', borderRadius: 4, padding: 8, marginBottom: 6 }} data-testid={`interaction-${inter.id}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700 }}>{inter.subject}</span>
                        <span style={{ fontSize: 11, color: 'var(--lw-muted)' }}>{new Date(inter.created_at).toLocaleString()}</span>
                      </div>
                      <p style={{ fontSize: 12.5 }}>{inter.content}</p>
                      <p style={{ fontSize: 11, color: 'var(--lw-muted)', marginTop: 3 }}>By: {inter.created_by_name}</p>
                    </div>
                  ))
                )}
              </div>
              <div className="lw-btnrow">
                <button className="lw-btn primary" onClick={handleAddNote} data-testid="add-note-button">+ Add Note</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default CarrierDetail;
